import logger from '@ayocore/platform/common/helpers/logger';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { useUserRepository } from '@ayocore/platform/identity/client/rpc';
import type { ActivityMentionUser } from '../types/activity-mention-user';
import { useDebouncedSearchWithCache } from './useDebouncedSearchWithCache';

const MENTION_USERS_LIMIT = 12;

type UseActivityMentionUsersSearchParams = {
  enabled: boolean;
  query: string;
};

type UseActivityMentionUsersSearchResult = {
  remoteMentionUsers: ActivityMentionUser[];
  mentionsLoading: boolean;
};

/**
 * Local data hook for mention suggestions from identity users.
 * Keeps async/caching logic out of presentation component.
 */
export function useActivityMentionUsersSearch({
  enabled,
  query,
}: UseActivityMentionUsersSearchParams): UseActivityMentionUsersSearchResult {
  const userRepository = useUserRepository();
  const currentUserId = useAuthController(s => s.user?.id);

  const normalizedQuery = query.trim().toLowerCase();

  const { results, loading } = useDebouncedSearchWithCache<ActivityMentionUser>({
    enabled,
    cacheKey: normalizedQuery,
    fetcher: async () => {
      // onlyHumanActive: is_active=true И system_code IS NULL — отсекаем
      // ботов/системных юзеров (LLM-агенты, integration users и т.п.),
      // которых упоминать смысла нет.
      let repository = userRepository.onlyHumanActive();
      if (normalizedQuery.length > 0) {
        repository = repository.search(normalizedQuery);
      }

      const rows = await repository.orderBy('name', 'asc').limit(MENTION_USERS_LIMIT);
      const users = Array.isArray(rows) ? rows : [];
      return users
        .filter(user => user.id > 0 && (!currentUserId || user.id !== currentUserId))
        .map(user => ({
          id: user.id,
          label: user.name || user.email || `Пользователь #${user.id}`,
          searchText: `${(user.name || '').toLowerCase()} ${(user.email || '').toLowerCase()} ${user.id}`,
        }));
    },
    onError: error => {
      logger.warn('[useActivityMentionUsersSearch] Failed to search users for mentions', {
        query: normalizedQuery,
        error,
      });
    },
  });

  return {
    remoteMentionUsers: results,
    mentionsLoading: loading,
  };
}
